import { useCallback, useEffect, useMemo, useState, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Play,
  X,
  CheckCircle2,
  ShieldCheck,
  AlertTriangle,
  Plus,
  Minus,
  Trash2,
  Clock,
  Pencil,
  Banknote,
  Coins,
  RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DENOMINATIONS,
  FIELD_LABELS,
  REVENUE_FIELDS,
  SHORTAGE_TYPE_LABELS,
  StatusBadge,
  formatDate,
  formatDay,
  formatFt,
} from "@/pages/daily_revenues/helpers";
import DailyRevenueFormDialog from "@/pages/daily_revenues/DailyRevenueFormDialog";

export default function DailyRevenueDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  const [openShortage, setOpenShortage] = useState(false);
  const [openSurplus, setOpenSurplus] = useState(false);
  const [openResolve, setOpenResolve] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/daily-revenues/${id}`);
      setItem(data);
    } catch {
      toast.error("Bejegyzés nem található.");
      navigate("/daily-revenues");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const doAction = async (path, body) => {
    try {
      const { data } = await api.post(`/daily-revenues/${id}${path}`, body || {});
      setItem(data);
      return data;
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Művelet sikertelen."
      );
      throw e;
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Törlöd ezt a bejegyzést?")) return;
    try {
      await api.delete(`/daily-revenues/${id}`);
      toast.success("Törölve.");
      navigate("/daily-revenues");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Törlés sikertelen.");
    }
  };

  if (loading || !item) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  const enabledFields = item.fields_snapshot || {};
  const status = item.status;
  const isNew = status === "new";
  const isVerifying = status === "verifying";
  const isShortage = status === "shortage";

  return (
    <div className="space-y-6 px-4 py-6 sm:px-6 lg:px-8" data-testid="rev-detail-page">
      <button
        onClick={() => navigate("/daily-revenues")}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" /> Vissza a listához
      </button>

      <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight">
                {formatDay(item.date)} · {item.site_name}
              </h1>
              <StatusBadge status={status} />
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {item.company_name && <>{item.company_name} · </>}
              Rögzítette: <span className="font-medium text-foreground">{item.created_by_name}</span>
              {" · "}{formatDate(item.created_at)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isNew && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOpenEdit(true)}
                  data-testid="rev-edit"
                >
                  <Pencil className="mr-1.5 h-4 w-4" /> Szerkesztés
                </Button>
                <Button
                  size="sm"
                  className="bg-sky-600 text-white hover:bg-sky-700"
                  onClick={() => doAction("/start-verification")}
                  data-testid="rev-start-verify"
                >
                  <Play className="mr-1.5 h-4 w-4" /> Ellenőrzés indítása
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onDelete}
                  className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                  data-testid="rev-delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
            {isVerifying && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => doAction("/cancel-verification")}
                data-testid="rev-cancel-verify"
              >
                <X className="mr-1.5 h-4 w-4" /> Ellenőrzés megszakítása
              </Button>
            )}
            {isShortage && (
              <Button
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                onClick={() => setOpenResolve(true)}
                data-testid="rev-resolve-shortage"
              >
                <ShieldCheck className="mr-1.5 h-4 w-4" /> Hiány rendezve
              </Button>
            )}
          </div>
        </div>
      </section>

      <AmountsCard item={item} enabledFields={enabledFields} />

      {isVerifying && (
        <VerificationPanel
          item={item}
          enabledFields={enabledFields}
          onSubmitted={(data) => setItem(data)}
        />
      )}

      {item.verification && !isVerifying && (
        <VerificationSummary item={item} enabledFields={enabledFields} />
      )}

      <ShortagesCard
        item={item}
        canEdit={isVerifying || isShortage}
        onAdd={() => setOpenShortage(true)}
        onRemove={async (sid) => {
          try {
            const { data } = await api.delete(`/daily-revenues/${id}/shortages/${sid}`);
            setItem(data);
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
          }
        }}
      />

      <SurplusesCard
        item={item}
        canEdit={isVerifying || isShortage}
        onAdd={() => setOpenSurplus(true)}
        onRemove={async (sid) => {
          try {
            const { data } = await api.delete(`/daily-revenues/${id}/surpluses/${sid}`);
            setItem(data);
          } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
          }
        }}
      />

      <TimelineCard timeline={item.timeline} />

      <DailyRevenueFormDialog
        open={openEdit}
        onOpenChange={setOpenEdit}
        initial={item}
        onSaved={(updated) => setItem(updated)}
      />

      <ShortageDialog
        open={openShortage}
        onOpenChange={setOpenShortage}
        entryId={id}
        enabledFields={enabledFields}
        onAdded={(data) => { setItem(data); setOpenShortage(false); }}
      />

      <SurplusDialog
        open={openSurplus}
        onOpenChange={setOpenSurplus}
        entryId={id}
        enabledFields={enabledFields}
        onAdded={(data) => { setItem(data); setOpenSurplus(false); }}
      />

      <ResolveShortagesDialog
        open={openResolve}
        onOpenChange={setOpenResolve}
        entryId={id}
        onResolved={(data) => { setItem(data); setOpenResolve(false); }}
      />
    </div>
  );
}

// ----- Sub-components -----

function AmountsCard({ item, enabledFields }) {
  const total = REVENUE_FIELDS.reduce(
    (s, f) => s + (Number(item.amounts?.[f]) || 0),
    0
  );
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
      <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Rögzített részösszegek
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        {REVENUE_FIELDS.filter((f) => enabledFields[f]).map((f) => (
          <div
            key={f}
            className="rounded-lg border border-border bg-muted/10 p-3"
            data-testid={`amount-${f}`}
          >
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {FIELD_LABELS[f]}
            </p>
            <p className="mt-1 text-lg font-semibold tabular-nums">
              {formatFt(item.amounts?.[f])}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between rounded-lg border border-border bg-foreground/[0.04] px-4 py-3">
        <span className="font-medium">Összes bevétel</span>
        <span className="text-base font-bold tabular-nums" data-testid="total-amount">
          {formatFt(total)}
        </span>
      </div>
      {item.notes && (
        <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Megjegyzés
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm">{item.notes}</p>
        </div>
      )}
    </section>
  );
}

function VerificationPanel({ item, enabledFields, onSubmitted }) {
  const enabledList = REVENUE_FIELDS.filter((f) => enabledFields[f]);
  // Envelope: denomination → count string
  const [envelope, setEnvelope] = useState(() => {
    const o = {};
    DENOMINATIONS.forEach((d) => { o[d] = ""; });
    return o;
  });
  // Recheck: field → string value
  const [recheck, setRecheck] = useState(() => {
    const o = {};
    enabledList.forEach((f) => { o[f] = ""; });
    return o;
  });
  // Az „override" funkció eltávolítva — a user explicit kérése: nem lehet
  // felülírni az eredetit. Helyette egy informatív értesítés ugrik fel, ha
  // a megadott érték nagyobb (= többlet keletkezik). A többletet a backend
  // automatikusan rögzíti.
  const [surplusNotice, setSurplusNotice] = useState(null); // field name
  const [submitting, setSubmitting] = useState(false);

  const envelopeTotal = useMemo(
    () =>
      DENOMINATIONS.reduce(
        (s, d) => s + (Number(envelope[d]) || 0) * d,
        0
      ),
    [envelope]
  );
  const cashExpected = Number(item.amounts?.cash) || 0;
  const cashMatch = !enabledFields.cash || Math.abs(envelopeTotal - cashExpected) < 0.01;

  // A leszámolt borítékösszeg automatikusan tükröződjön a Készpénz mezőben.
  useEffect(() => {
    if (!enabledFields.cash) return;
    const str = envelopeTotal > 0 ? String(envelopeTotal) : "";
    setRecheck((p) => {
      if (p.cash === str) return p;
      return { ...p, cash: str };
    });
  }, [envelopeTotal, enabledFields.cash]);

  // Blur után: ha a megadott érték a CASH mezőben nagyobb mint az eredeti,
  // egy figyelmeztető dialógust mutatunk — a többlet rögzítésre kerül, a user
  // vagy javítja, vagy elfogadja. Nincs lehetőség az eredeti felülírására.
  const maybeShowSurplusNotice = (field) => {
    const orig = Number(item.amounts?.[field]) || 0;
    const rv = recheck[field];
    if (rv === "" || rv === undefined || rv === null) return;
    const num = Number(rv);
    if (Number.isNaN(num)) return;
    if (num <= orig) return; // csak ha tényleges többlet
    setSurplusNotice(field);
  };

  const onSubmit = async () => {
    const envelopePayload = {};
    DENOMINATIONS.forEach((d) => {
      const c = Number(envelope[d]) || 0;
      if (c > 0) envelopePayload[String(d)] = c;
    });
    const recheckPayload = {};
    enabledList.forEach((f) => {
      recheckPayload[f] = Number(recheck[f]) || 0;
    });
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/daily-revenues/${item._id}/submit-verification`,
        {
          envelope: envelopePayload,
          rechecked_amounts: recheckPayload,
          overrides: {}, // mindig üres — felülírás nem engedélyezett
        }
      );
      if (data.status === "approved") {
        toast.success("Ellenőrzés sikeres – lezárva.");
      } else {
        toast.warning("Ellenőrzés rögzítve – a bejegyzés hiány státuszba került.");
      }
      onSubmitted?.(data);
    } catch (e) {
      toast.error(
        formatApiError(e?.response?.data?.detail) || "Mentés sikertelen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const allRecheckFilled = enabledList.every(
    (f) => recheck[f] !== "" && recheck[f] != null
  );

  return (
    <section
      className="space-y-4 rounded-2xl border border-sky-200 bg-sky-50/40 p-6 shadow-sm dark:border-sky-500/30 dark:bg-sky-500/[0.04]"
      data-testid="verification-panel"
    >
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-sky-700 dark:text-sky-300" />
        <p className="text-sm font-semibold text-sky-900 dark:text-sky-200">
          Ellenőrzés folyamatban
        </p>
      </div>

      {/* Envelope counting */}
      {enabledFields.cash && (
        <EnvelopeCounter
          envelope={envelope}
          setEnvelope={setEnvelope}
          envelopeTotal={envelopeTotal}
          cashExpected={cashExpected}
          cashMatch={cashMatch}
        />
      )}

      {/* Amount entry */}
      <div className="rounded-lg border border-border bg-white p-4 shadow-sm dark:bg-background">
        <Label className="text-xs font-semibold uppercase tracking-wider">
          Összegek megadása
        </Label>
        <p className="mb-3 mt-0.5 text-[11px] text-muted-foreground">
          Add meg az összegeket. Csak akkor kérdezünk rá felülírásra, ha a
          megadott érték magasabb az eredetinél — és csak miután kikattintasz
          a mezőből.
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          {enabledList.map((f) => {
            const orig = Number(item.amounts?.[f]) || 0;
            const rv = recheck[f];
            const num = rv === "" ? null : Number(rv);
            const diff = num != null && Math.abs(num - orig) > 0.01;
            const isCashLocked = f === "cash"; // cash mező a borítékból tölt
            return (
              <div key={f} className="space-y-1">
                <Label htmlFor={`rc-${f}`} className="text-[11px]">
                  {FIELD_LABELS[f]} — eredeti {formatFt(orig)}
                  {isCashLocked && (
                    <span className="ml-1 rounded bg-foreground/[0.06] px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Címletezésből
                    </span>
                  )}
                </Label>
                {isCashLocked ? (
                  // Read-only: formatált megjelenítés ezres elválasztóval.
                  <div
                    id={`rc-${f}`}
                    data-testid={`rc-${f}`}
                    className={cn(
                      "flex h-10 cursor-not-allowed items-center rounded-md border border-input bg-muted/40 px-3 text-sm font-semibold tabular-nums text-foreground/85",
                      diff && (num - orig) > 0 && "border-emerald-400 text-emerald-700",
                      diff && (num - orig) < 0 && "border-rose-400 text-rose-700",
                    )}
                  >
                    {num != null && num > 0 ? formatFt(num) : "—"}
                  </div>
                ) : (
                  <Input
                    id={`rc-${f}`}
                    type="number"
                    min="0"
                    step="1"
                    value={rv}
                    placeholder="Összeg…"
                    onChange={(e) =>
                      setRecheck((p) => ({ ...p, [f]: e.target.value }))
                    }
                    onBlur={() => maybeShowSurplusNotice(f)}
                    className={cn(
                      diff
                        ? (num - orig) > 0
                          ? "border-emerald-400 focus-visible:ring-emerald-200"
                          : "border-rose-400 focus-visible:ring-rose-200"
                        : "",
                    )}
                    data-testid={`rc-${f}`}
                  />
                )}
                {diff && (() => {
                  const delta = num - orig;
                  const isSurplus = delta > 0;
                  return (
                    <p className="flex items-center gap-1 text-[11px]">
                      <AlertTriangle
                        className={cn(
                          "h-3 w-3",
                          isSurplus ? "text-emerald-600" : "text-amber-600",
                        )}
                      />
                      {isSurplus ? "Többlet" : "Hiány"}:{" "}
                      <b
                        className={cn(
                          "tabular-nums",
                          isSurplus ? "text-emerald-700" : "text-rose-700",
                        )}
                      >
                        {isSurplus ? "+" : ""}
                        {formatFt(delta)}
                      </b>
                    </p>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[11px] text-muted-foreground">
          Beküldéskor: ha minden egyezik → Lezárva. Eltérésnél hiány vagy
          többlet státusz keletkezik.
        </p>
        <Button
          onClick={onSubmit}
          disabled={submitting || !allRecheckFilled}
          className="bg-sky-600 text-white hover:bg-sky-700"
          data-testid="submit-verification"
        >
          {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
          <CheckCircle2 className="mr-1.5 h-4 w-4" /> Ellenőrzés lezárása
        </Button>
      </div>

      {/* Surplus notice dialog — figyelmeztetés, nem felülírás */}
      <Dialog
        open={!!surplusNotice}
        onOpenChange={(v) => { if (!v) setSurplusNotice(null); }}
      >
        <DialogContent data-testid="surplus-notice-dialog">
          <DialogHeader>
            <DialogTitle>Többlet észlelve</DialogTitle>
            <DialogDescription>
              {surplusNotice && (
                <>
                  A megadott érték nagyobb mint az eredeti összeg —{" "}
                  <b>emiatt többlet kerül rögzítésre</b>.
                  <br/>
                  <span className="mt-2 inline-block text-foreground">
                    Mező: <b>{FIELD_LABELS[surplusNotice]}</b><br/>
                    Eredeti: <b className="tabular-nums">{formatFt(item.amounts?.[surplusNotice])}</b><br/>
                    Megadott: <b className="tabular-nums">{formatFt(recheck[surplusNotice])}</b>{" "}
                    <span className="text-emerald-700">
                      (+{formatFt(Number(recheck[surplusNotice]) - Number(item.amounts?.[surplusNotice] || 0))})
                    </span>
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                // „Javítás" — bezárjuk a dialógust, és a felhasználó a mezőben
                // kijavíthatja az értéket. Kis fókusz a mezőre kényelmesebbé teszi.
                const fid = `rc-${surplusNotice}`;
                setSurplusNotice(null);
                setTimeout(() => {
                  const el = document.getElementById(fid);
                  if (el && typeof el.focus === "function") el.focus();
                }, 50);
              }}
              data-testid="surplus-notice-fix"
            >
              Javítás
            </Button>
            <Button
              onClick={() => setSurplusNotice(null)}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="surplus-notice-ok"
            >
              Rendben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

function VerificationSummary({ item, enabledFields }) {
  const v = item.verification;
  if (!v) return null;
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
      <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Ellenőrzés összegzése · {v.verified_by_name} · {formatDate(v.verified_at)}
      </p>
      {enabledFields.cash && v.envelope && Object.keys(v.envelope).length > 0 && (
        <div className="mb-4">
          <Label className="text-[11px]">Boríték címletek</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {Object.entries(v.envelope).map(([denom, count]) => (
              <span
                key={denom}
                className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/30 px-2.5 py-0.5 text-[11px] tabular-nums"
              >
                {formatFt(Number(denom)).replace(" Ft", "")} × <b>{count}</b>
              </span>
            ))}
          </div>
          <p className="mt-2 text-xs">
            Boríték össz.: <b className="tabular-nums">{formatFt(v.envelope_total)}</b>
            {" · "}
            Készpénz össz.: <b className="tabular-nums">{formatFt(v.cash_expected)}</b>
            {" · "}
            {v.cash_match
              ? <span className="text-emerald-600 font-semibold">EGYEZIK</span>
              : <span className="text-rose-600 font-semibold">ELTÉRÉS</span>}
          </p>
        </div>
      )}
      {v.discrepancies && Object.keys(v.discrepancies).length > 0 && (
        <div>
          <Label className="text-[11px]">Részösszeg eltérések</Label>
          <ul className="mt-2 space-y-1.5">
            {Object.entries(v.discrepancies).map(([f, d]) => {
              const delta = (d.rechecked ?? 0) - (d.original ?? 0);
              const isSurplus = delta > 0;
              return (
                <li key={f} className="text-sm">
                  <b>{FIELD_LABELS[f]}:</b> eredeti{" "}
                  <span className="tabular-nums">{formatFt(d.original)}</span> →
                  megadott{" "}
                  <span className="tabular-nums">{formatFt(d.rechecked)}</span>
                  {" · "}
                  {d.overridden ? (
                    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">
                      Felülírva
                    </span>
                  ) : isSurplus ? (
                    <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                      Többlet +{formatFt(delta)}
                    </span>
                  ) : (
                    <span className="rounded bg-rose-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-rose-800 dark:bg-rose-500/15 dark:text-rose-300">
                      Hiány {formatFt(delta)}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </section>
  );
}

function ShortagesCard({ item, canEdit, onAdd, onRemove }) {
  const list = item.shortages || [];
  if (list.length === 0 && !canEdit) return null;
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
      <header className="mb-3 flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Hiány lista ({list.length})
        </p>
        {canEdit && (
          <Button size="sm" variant="outline" onClick={onAdd} data-testid="add-shortage">
            <Plus className="h-3.5 w-3.5" /> Új hiány
          </Button>
        )}
      </header>
      {list.length === 0 ? (
        <p className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-4 text-center text-xs text-muted-foreground">
          Nincs hiány.
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {list.map((s) => (
            <li
              key={s._id}
              className="flex flex-wrap items-start justify-between gap-3 py-3"
              data-testid={`shortage-${s._id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <b>{SHORTAGE_TYPE_LABELS[s.type] || s.type}</b>
                  {" · "}
                  <span className="tabular-nums font-semibold text-rose-600">
                    {formatFt(s.amount)}
                  </span>
                  {s.auto && (
                    <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-amber-800">
                      auto
                    </span>
                  )}
                  {s.resolved && (
                    <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800">
                      rendezve
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{s.reason}</p>
                {s.resolution_note && (
                  <p className="mt-1 text-xs italic text-muted-foreground">
                    Megoldás: {s.resolution_note}
                  </p>
                )}
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {s.created_by_name} · {formatDate(s.created_at)}
                </p>
              </div>
              {canEdit && !s.resolved && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-500/10"
                  onClick={() => onRemove(s._id)}
                  data-testid={`remove-shortage-${s._id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SurplusesCard({ item, canEdit, onAdd, onRemove }) {
  const list = item.surpluses || [];
  if (list.length === 0 && !canEdit) return null;
  return (
    <section
      data-testid="surpluses-card"
      className="rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/40 to-white p-6 shadow-sm dark:border-emerald-500/20 dark:from-emerald-500/[0.04] dark:to-[hsl(var(--sipx-elevated))]"
    >
      <header className="mb-3 flex items-center justify-between">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700/90">
          <span className="grid h-5 w-5 place-items-center rounded-md bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
            <Plus className="h-3 w-3" strokeWidth={2.5} />
          </span>
          Többlet lista ({list.length})
        </p>
        {canEdit && (
          <Button
            size="sm"
            variant="outline"
            onClick={onAdd}
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-300"
            data-testid="add-surplus"
          >
            <Plus className="h-3.5 w-3.5" /> Új többlet
          </Button>
        )}
      </header>
      {list.length === 0 ? (
        <p className="rounded-md border border-dashed border-emerald-200 bg-emerald-50/30 px-3 py-4 text-center text-xs text-emerald-700/80 dark:border-emerald-500/30 dark:bg-emerald-500/[0.06] dark:text-emerald-300">
          Nincs többlet.
        </p>
      ) : (
        <ul className="divide-y divide-emerald-200/60 dark:divide-emerald-500/15">
          {list.map((s) => (
            <li
              key={s._id}
              className="flex flex-wrap items-start justify-between gap-3 py-3"
              data-testid={`surplus-${s._id}`}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <b>{SHORTAGE_TYPE_LABELS[s.type] || s.type}</b>
                  {" · "}
                  <span className="tabular-nums font-semibold text-emerald-700 dark:text-emerald-300">
                    +{formatFt(s.amount)}
                  </span>
                  {s.auto && (
                    <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
                      auto
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">{s.reason}</p>
                <p className="mt-1 text-[10px] text-muted-foreground">
                  {s.created_by_name} · {formatDate(s.created_at)}
                </p>
              </div>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:hover:bg-emerald-500/10"
                  onClick={() => onRemove(s._id)}
                  data-testid={`remove-surplus-${s._id}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function TimelineCard({ timeline }) {
  const list = timeline || [];
  return (
    <section className="rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm dark:border-border dark:bg-[hsl(var(--sipx-elevated))]">
      <p className="mb-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Idővonal
      </p>
      <ol className="space-y-3">
        {list.length === 0 && (
          <li className="text-sm text-muted-foreground">Még nincs esemény.</li>
        )}
        {list.map((ev) => (
          <li key={ev._id} className="flex items-start gap-3 text-sm">
            <div className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-foreground/[0.06]">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            </div>
            <div>
              <p className="leading-snug">{ev.text}</p>
              <p className="text-[11px] text-muted-foreground">
                {ev.author_name} · {formatDate(ev.created_at)}
              </p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function ShortageDialog({ open, onOpenChange, entryId, enabledFields, onAdded }) {
  const [type, setType] = useState("other");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setType("other"); setAmount(""); setReason(""); }
  }, [open]);

  const typeOptions = ["cash", "card", "wolt", "foodora", "kiosk", "app", "other"]
    .filter((t) => t === "other" || enabledFields[t]);

  const onSubmit = async () => {
    if (!(Number(amount) > 0)) {
      toast.error("Pozitív összeg kell.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Indok kötelező.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/daily-revenues/${entryId}/shortages`, {
        type, amount: Number(amount), reason: reason.trim(),
      });
      toast.success("Hiány hozzáadva.");
      onAdded?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="shortage-dialog">
        <DialogHeader>
          <DialogTitle>Új hiány rögzítése</DialogTitle>
          <DialogDescription>
            Adj meg típust, összeget és indokot. A hiány a &bdquo;Hiány lista&rdquo; alá kerül.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="stype">Típus</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger id="stype" data-testid="shortage-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((t) => (
                    <SelectItem key={t} value={t}>
                      {SHORTAGE_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="samt">Összeg (Ft)</Label>
              <Input
                id="samt"
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="shortage-amount"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="sreason">Indok</Label>
            <Textarea
              id="sreason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="shortage-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={onSubmit} disabled={saving} data-testid="confirm-add-shortage">
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Hozzáadás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SurplusDialog({ open, onOpenChange, entryId, enabledFields, onAdded }) {
  const [type, setType] = useState("other");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setType("other");
      setAmount("");
      setReason("");
    }
  }, [open]);

  const typeOptions = ["cash", "card", "wolt", "foodora", "kiosk", "app", "other"]
    .filter((t) => t === "other" || enabledFields[t]);

  const onSubmit = async () => {
    if (!(Number(amount) > 0)) {
      toast.error("Pozitív összeg kell.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Indok kötelező.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/daily-revenues/${entryId}/surpluses`, {
        type,
        amount: Number(amount),
        reason: reason.trim(),
      });
      toast.success("Többlet hozzáadva.");
      onAdded?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="surplus-dialog">
        <DialogHeader>
          <DialogTitle>Új többlet rögzítése</DialogTitle>
          <DialogDescription>
            Adj meg típust, összeget és indokot. A többlet a &bdquo;Többlet lista&rdquo; alá kerül.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="surp-type">Típus</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger id="surp-type" data-testid="surplus-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((t) => (
                    <SelectItem key={t} value={t}>
                      {SHORTAGE_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="surp-amt">Összeg (Ft)</Label>
              <Input
                id="surp-amt"
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="surplus-amount"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="surp-reason">Indok</Label>
            <Textarea
              id="surp-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="surplus-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="confirm-surplus"
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <Plus className="mr-1.5 h-4 w-4" /> Hozzáadás
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResolveShortagesDialog({ open, onOpenChange, entryId, onResolved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setNote(""); }, [open]);

  const onSubmit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(
        `/daily-revenues/${entryId}/resolve-shortages`,
        { note: note.trim() || null }
      );
      toast.success("Hiány rendezve.");
      onResolved?.(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Sikertelen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="resolve-dialog">
        <DialogHeader>
          <DialogTitle>Hiány rendezése és jóváhagyás</DialogTitle>
          <DialogDescription>
            Megerősíted, hogy a hiányt rendezted? A bejegyzés &bdquo;Hiány rendezve,
            jóváhagyva&rdquo; státuszba kerül.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <Label htmlFor="rnote">Megjegyzés (opcionális)</Label>
          <Textarea
            id="rnote"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Pl. pénztáros pótolta, vevő átutalta…"
            data-testid="resolve-note"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Mégse
          </Button>
          <Button
            onClick={onSubmit}
            disabled={saving}
            className="bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="confirm-resolve"
          >
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <ShieldCheck className="mr-1.5 h-4 w-4" /> Rendezve, jóváhagyom
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// =============================================================
// EnvelopeCounter — designed envelope-counting widget for cash
// verification. Bills + coins grouped, color-coded denomination
// pills, ± stepper, clean live totals.
// =============================================================

// Approximate background tones for each Hungarian banknote /
// coin, used as a subtle visual marker on each row's pill.
// Each tone provides 3 colour layers: a soft chip background,
// a left "denomination stripe" colour, and an active-ring tint.
const DENOM_STYLES = {
  // Bills
  20000: {
    chip: "from-cyan-100/80 to-cyan-50 text-cyan-900 dark:from-cyan-500/20 dark:to-cyan-500/[0.08] dark:text-cyan-100",
    stripe: "bg-cyan-500",
    ring: "ring-cyan-200/60 dark:ring-cyan-500/30",
  },
  10000: {
    chip: "from-orange-100/80 to-orange-50 text-orange-900 dark:from-orange-500/20 dark:to-orange-500/[0.08] dark:text-orange-100",
    stripe: "bg-orange-500",
    ring: "ring-orange-200/60 dark:ring-orange-500/30",
  },
  5000: {
    chip: "from-emerald-100/80 to-emerald-50 text-emerald-900 dark:from-emerald-500/20 dark:to-emerald-500/[0.08] dark:text-emerald-100",
    stripe: "bg-emerald-500",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/30",
  },
  2000: {
    chip: "from-violet-100/80 to-violet-50 text-violet-900 dark:from-violet-500/20 dark:to-violet-500/[0.08] dark:text-violet-100",
    stripe: "bg-violet-500",
    ring: "ring-violet-200/60 dark:ring-violet-500/30",
  },
  1000: {
    chip: "from-sky-100/80 to-sky-50 text-sky-900 dark:from-sky-500/20 dark:to-sky-500/[0.08] dark:text-sky-100",
    stripe: "bg-sky-500",
    ring: "ring-sky-200/60 dark:ring-sky-500/30",
  },
  500: {
    chip: "from-amber-100/80 to-amber-50 text-amber-900 dark:from-amber-500/20 dark:to-amber-500/[0.08] dark:text-amber-100",
    stripe: "bg-amber-500",
    ring: "ring-amber-200/60 dark:ring-amber-500/30",
  },
  // Coins — warmer for gold-coloured, cool for silver-coloured
  200: {
    chip: "from-yellow-100/80 to-yellow-50 text-yellow-900 dark:from-yellow-500/20 dark:to-yellow-500/[0.08] dark:text-yellow-100",
    stripe: "bg-yellow-500",
    ring: "ring-yellow-200/60 dark:ring-yellow-500/30",
  },
  100: {
    chip: "from-yellow-100/80 to-yellow-50 text-yellow-900 dark:from-yellow-500/20 dark:to-yellow-500/[0.08] dark:text-yellow-100",
    stripe: "bg-yellow-400",
    ring: "ring-yellow-200/60 dark:ring-yellow-500/30",
  },
  50: {
    chip: "from-slate-100/80 to-slate-50 text-slate-700 dark:from-slate-500/15 dark:to-slate-500/[0.06] dark:text-slate-200",
    stripe: "bg-slate-400",
    ring: "ring-slate-200/60 dark:ring-slate-500/30",
  },
  20: {
    chip: "from-slate-100/80 to-slate-50 text-slate-700 dark:from-slate-500/15 dark:to-slate-500/[0.06] dark:text-slate-200",
    stripe: "bg-slate-400",
    ring: "ring-slate-200/60 dark:ring-slate-500/30",
  },
  10: {
    chip: "from-stone-100/80 to-stone-50 text-stone-700 dark:from-stone-500/15 dark:to-stone-500/[0.06] dark:text-stone-200",
    stripe: "bg-stone-400",
    ring: "ring-stone-200/60 dark:ring-stone-500/30",
  },
  5: {
    chip: "from-stone-100/80 to-stone-50 text-stone-700 dark:from-stone-500/15 dark:to-stone-500/[0.06] dark:text-stone-200",
    stripe: "bg-stone-400",
    ring: "ring-stone-200/60 dark:ring-stone-500/30",
  },
};

const BILL_DENOMS = [20000, 10000, 5000, 2000, 1000, 500];
const COIN_DENOMS = [200, 100, 50, 20, 10, 5];

function EnvelopeCounter({ envelope, setEnvelope, envelopeTotal, cashExpected, cashMatch }) {
  const diff = envelopeTotal - cashExpected;
  const totalCount = [...BILL_DENOMS, ...COIN_DENOMS].reduce(
    (s, d) => s + (Number(envelope[d]) || 0),
    0
  );
  const ratio = cashExpected > 0
    ? Math.max(0, Math.min(1, envelopeTotal / cashExpected))
    : 0;
  const tone = cashMatch
    ? "emerald"
    : envelopeTotal > cashExpected
    ? "amber"
    : "rose";

  const clearAll = () => {
    if (!window.confirm("Boríték címletek nullázása?")) return;
    setEnvelope((p) => {
      const o = { ...p };
      Object.keys(o).forEach((k) => { o[k] = ""; });
      return o;
    });
  };

  return (
    <div
      className="overflow-hidden rounded-xl border border-border bg-white shadow-sm dark:bg-background"
      data-testid="envelope-counter"
    >
      {/* Header — live totals */}
      <header className="border-b border-border bg-gradient-to-br from-foreground/[0.03] to-transparent px-5 py-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5 text-muted-foreground/80">
              <Coins className="h-3.5 w-3.5" strokeWidth={2} />
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em]">
                Boríték
              </span>
            </div>
            <p className="mt-0.5 text-sm font-semibold">Címlet szerinti számolás</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-3 py-1 text-[11px] font-medium text-muted-foreground hover:text-foreground"
              data-testid="env-clear"
            >
              <RotateCcw className="h-3 w-3" /> Nullázás
            </button>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Boríték / Készpénz
              </p>
              <p className="text-base font-semibold tabular-nums">
                <span
                  className={
                    tone === "emerald"
                      ? "text-emerald-600 dark:text-emerald-400"
                      : tone === "amber"
                      ? "text-amber-600 dark:text-amber-400"
                      : "text-rose-600 dark:text-rose-400"
                  }
                  data-testid="envelope-total"
                >
                  {formatFt(envelopeTotal)}
                </span>{" "}
                <span className="text-muted-foreground">/ {formatFt(cashExpected)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-foreground/[0.06]">
          <div
            className={`h-full transition-all duration-300 ${
              tone === "emerald"
                ? "bg-emerald-500"
                : tone === "amber"
                ? "bg-amber-500"
                : "bg-rose-500"
            }`}
            style={{ width: `${Math.min(ratio * 100, 100).toFixed(1)}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>
            {totalCount > 0
              ? `Összesen ${totalCount} db címlet beírva`
              : "Még nincs címlet rögzítve"}
          </span>
          {!cashMatch && envelopeTotal > 0 && (
            <span
              className={`font-medium tabular-nums ${
                diff > 0 ? "text-amber-700" : "text-rose-700"
              }`}
              data-testid="env-diff"
            >
              {diff > 0 ? "+" : ""}
              {formatFt(diff)}
            </span>
          )}
        </div>
      </header>

      {/* Bills */}
      <DenomGroup
        title="Bankjegyek"
        icon={Banknote}
        denoms={BILL_DENOMS}
        envelope={envelope}
        setEnvelope={setEnvelope}
      />

      {/* Coins */}
      <DenomGroup
        title="Érmék"
        icon={Coins}
        denoms={COIN_DENOMS}
        envelope={envelope}
        setEnvelope={setEnvelope}
      />

      {!cashMatch && envelopeTotal > 0 && (
        <div
          className={`flex items-start gap-2 border-t px-5 py-3 text-xs ${
            diff > 0
              ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
              : "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200"
          }`}
          data-testid="cash-mismatch"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <p>
            A boríték címletösszesítése nem egyezik a megadott Készpénzzel.{" "}
            Különbség: <b className="tabular-nums">{formatFt(diff)}</b>
            {" "}— beküldéskor automatikus hiány-tétel kerül felvételre.
          </p>
        </div>
      )}
    </div>
  );
}

function DenomGroup({ title, icon: Icon, denoms, envelope, setEnvelope }) {
  const groupTotal = denoms.reduce(
    (s, d) => s + (Number(envelope[d]) || 0) * d,
    0
  );
  const filledCount = denoms.filter((d) => Number(envelope[d]) > 0).length;

  // Stable per-denom setter so each <DenomRow /> only re-renders when its
  // own `count` actually changes. Without this, typing into a single input
  // would re-render all 18 sibling rows on every keystroke (visible typing
  // lag on slower machines).
  const handleChange = useCallback(
    (d, v) => setEnvelope((p) => ({ ...p, [d]: v })),
    [setEnvelope],
  );

  return (
    <div className="border-t border-border first:border-t-0">
      <header className="flex items-center justify-between bg-gradient-to-r from-foreground/[0.025] to-transparent px-5 py-3">
        <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          <span className="grid h-6 w-6 place-items-center rounded-md bg-foreground/[0.06]">
            <Icon className="h-3 w-3" strokeWidth={2} />
          </span>
          {title}
        </span>
        <span className="text-[11px] tabular-nums">
          {filledCount > 0 ? (
            <>
              <span className="text-muted-foreground">
                {filledCount} féle ·{" "}
              </span>
              <b className="text-foreground">{formatFt(groupTotal)}</b>
            </>
          ) : (
            <span className="text-muted-foreground/50">üres</span>
          )}
        </span>
      </header>
      <ul className="grid grid-cols-1 gap-2 px-3 py-3 sm:grid-cols-2 lg:grid-cols-3">
        {denoms.map((d) => (
          <DenomRow
            key={d}
            denom={d}
            count={envelope[d]}
            onChange={handleChange}
          />
        ))}
      </ul>
    </div>
  );
}

const DenomRow = memo(function DenomRow({ denom, count, onChange }) {
  const numCount = Number(count) || 0;
  const lineTotal = numCount * denom;
  const isActive = numCount > 0;
  const style = DENOM_STYLES[denom] || {};

  const set = (v) => {
    const n = Math.max(0, Math.floor(Number(v) || 0));
    onChange(denom, n === 0 ? "" : String(n));
  };
  const inc = () => set(numCount + 1);
  const dec = () => set(Math.max(0, numCount - 1));

  return (
    <li
      className={cn(
        "group relative overflow-hidden rounded-xl border bg-background transition-all",
        isActive
          ? "border-foreground/15 shadow-[0_2px_10px_-6px_rgba(15,23,42,0.18)] ring-1"
          : "border-border hover:border-foreground/20",
        isActive && style.ring,
      )}
      data-testid={`env-row-${denom}`}
    >
      {/* Coloured left stripe — visually anchors the denomination */}
      <span
        aria-hidden
        className={cn(
          "absolute inset-y-0 left-0 w-1 transition-opacity",
          style.stripe,
          isActive ? "opacity-100" : "opacity-40 group-hover:opacity-70",
        )}
      />

      <div className="flex items-center gap-3 px-3 py-2.5 pl-4">
        {/* Denomination chip — gradient, bigger, more designed */}
        <span
          className={cn(
            "inline-flex h-9 min-w-[88px] items-center justify-center whitespace-nowrap rounded-lg bg-gradient-to-br px-3 text-[13px] font-semibold tabular-nums",
            style.chip,
          )}
        >
          {formatFt(denom)}
        </span>

        <span className="text-[11px] font-medium text-muted-foreground/70">×</span>

        {/* Stepper */}
        <div className="ml-auto flex items-center">
          <button
            type="button"
            onClick={dec}
            disabled={numCount === 0}
            aria-label="csökkentés"
            className="grid h-8 w-8 place-items-center rounded-l-lg border border-r-0 border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-30 disabled:hover:bg-background"
            data-testid={`env-${denom}-dec`}
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="0"
            value={count}
            onChange={(e) => onChange(denom, e.target.value)}
            className={cn(
              "h-8 w-14 rounded-none border-x-0 border-y border-border bg-background px-1 text-center text-[13px] font-semibold tabular-nums focus-visible:ring-0 focus-visible:ring-offset-0",
              isActive ? "text-foreground" : "text-muted-foreground",
            )}
            data-testid={`env-${denom}`}
          />
          <button
            type="button"
            onClick={inc}
            aria-label="növelés"
            className="grid h-8 w-8 place-items-center rounded-r-lg border border-l-0 border-border bg-background text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            data-testid={`env-${denom}-inc`}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {/* Sub-total bar — only when active, animates in */}
      <div
        className={cn(
          "flex items-center justify-between border-t bg-foreground/[0.025] px-4 py-1.5 transition-all",
          isActive
            ? "border-border opacity-100"
            : "max-h-0 -mt-px border-transparent py-0 opacity-0",
        )}
      >
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Részösszeg
        </span>
        <span className="text-[13px] font-semibold tabular-nums">
          {formatFt(lineTotal)}
        </span>
      </div>
    </li>
  );
});
